import { useEffect, useRef, useState, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api, formatErr } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "sonner";
import { Heart, Users, ArrowLeft, Broadcast, GearSix, UserPlus, X, Check, Coins } from "@phosphor-icons/react";
import LiveSettingsDrawer from "../components/LiveSettingsDrawer";
import LiveChat from "../components/LiveChat";
import GiftPicker from "../pages/GiftPicker";
import GiftAnimation from "../components/GiftAnimation";
import LiveKitStage from "../components/LiveKitStage";

let heartId = 0;
const GUEST_MAX = 2;

export default function LiveStream() {
    const { liveId } = useParams();
    const nav = useNavigate();
    const { user } = useAuth();
    const [live, setLive] = useState(null);
    const [hearts, setHearts] = useState([]);
    const [tapBurst, setTapBurst] = useState(0);
    const [settingsOpen, setSettingsOpen] = useState(false);
    const [endingMode, setEndingMode] = useState(false);
    const [recordPickerOpen, setRecordPickerOpen] = useState(false);
    const [inviteHandled, setInviteHandled] = useState(false);
    const [respondingInvite, setRespondingInvite] = useState(false);
    const [giftOpen, setGiftOpen] = useState(false);
    const tapPending = useRef(0);
    const flushTimer = useRef(null);
    const stageRef = useRef(null);
    const videoRef = useRef(null);
    const streamRef = useRef(null);
    const fileRef = useRef(null);
    const giftFxRef = useRef(null);
    const mediaRecorderRef = useRef(null);
    const recordedChunksRef = useRef([]);
    const recordingMimeRef = useRef("video/webm");

    const load = useCallback(async () => {
        try {
            const { data } = await api.get(`/live/${liveId}`);
            setLive(data);
        } catch (e) {
            toast.error(formatErr(e.response?.data?.detail) || "Live stream unavailable");
            nav("/live");
        }
    }, [liveId, nav]);

    useEffect(() => { load(); }, [load]);

    // Poll for likes_count + setting updates from other viewers / host
    useEffect(() => {
        const t = setInterval(() => {
            api.get(`/live/${liveId}`).then(({ data }) => {
                setLive((cur) => {
                    if (!cur) return cur;
                    return { ...cur, ...data };
                });
            }).catch(() => {});
        }, 4000);
        return () => clearInterval(t);
    }, [liveId]);

    const isHost = user && live && live.user_id === user.id && live.status === "live";

    // Whether this user should publish to LiveKit (host always; guests when accepted)
    const isAcceptedGuest = !!user && !!live && (live.guests || [])
        .some((g) => g.user_id === user.id && (g.status === "accepted" || g.status === "on_stage"));
    const shouldPublish = isHost || isAcceptedGuest;
    const liveKitEnabled = live?.status === "live" && !live?.using_wallpaper;

    // Manage host camera stream — only when LiveKit is NOT in use (wallpaper mode)
    useEffect(() => {
        if (!isHost) return;
        if (liveKitEnabled) {
            // LiveKit owns the camera. Stop any local stream we previously held
            // and DO NOT call getUserMedia here — that would race with LiveKit
            // and cause "Camera in use by another app".
            if (streamRef.current) {
                streamRef.current.getTracks().forEach((t) => t.stop());
                streamRef.current = null;
            }
            if (videoRef.current) videoRef.current.srcObject = null;
            return;
        }
        const facing = live?.facing_mode || "user";
        const useWp = !!live?.using_wallpaper;
        // Stop previous stream
        if (streamRef.current) {
            streamRef.current.getTracks().forEach((t) => t.stop());
            streamRef.current = null;
        }
        if (useWp) {
            if (videoRef.current) videoRef.current.srcObject = null;
            return;
        }
        let cancelled = false;
        // Mobile-robust camera acquisition: try preferred, then ideal, then any
        const tryConstraints = async () => {
            const attempts = [
                { video: { facingMode: { ideal: facing } }, audio: true },
                { video: { facingMode: facing }, audio: true },
                { video: true, audio: true },
                { video: true, audio: false },
            ];
            for (const c of attempts) {
                try {
                    return await navigator.mediaDevices.getUserMedia(c);
                } catch (err) {
                    // Try next constraint set
                    console.warn("getUserMedia failed for constraints, trying next:", c, err.name);
                    if (err.name === "NotAllowedError" || err.name === "PermissionDeniedError") {
                        throw err;  // user denied permission — don't keep trying
                    }
                }
            }
            throw new Error("All camera constraint attempts failed");
        };

        if (!navigator.mediaDevices?.getUserMedia) {
            toast.error("Camera not supported on this browser. Try Chrome or Safari, and ensure HTTPS.");
            return;
        }

        tryConstraints()
            .then((stream) => {
                if (cancelled) { stream.getTracks().forEach((t) => t.stop()); return; }
                streamRef.current = stream;
                if (videoRef.current) {
                    videoRef.current.srcObject = stream;
                    videoRef.current.play().catch(() => {});
                }
                // Begin auto-recording for archive
                startRecording(stream);
            })
            .catch((err) => {
                const name = err?.name || "Error";
                if (name === "NotAllowedError" || name === "PermissionDeniedError") {
                    toast.error("Camera permission denied. Enable it in your browser settings and reload.");
                } else if (name === "NotFoundError" || name === "DevicesNotFoundError") {
                    toast.error("No camera found on this device.");
                } else if (name === "NotReadableError" || name === "TrackStartError") {
                    toast.error("Camera is in use by another app. Close it and retry.");
                } else if (name === "OverconstrainedError") {
                    toast.error("Camera doesn't support requested settings. Try switching front/back camera.");
                } else {
                    toast.error(`Camera unavailable (${name}). Switch to wallpaper from settings.`);
                }
            });
        return () => {
            cancelled = true;
            stopRecording();
            if (streamRef.current) {
                streamRef.current.getTracks().forEach((t) => t.stop());
                streamRef.current = null;
            }
        };
    }, [isHost, live?.facing_mode, live?.using_wallpaper, liveKitEnabled]);

    // ===== Auto-recording (MediaRecorder) =====
    const startRecording = useCallback((stream) => {
        if (!stream || !window.MediaRecorder) return;
        try {
            // Pick a supported MIME type (Safari prefers mp4, Chrome/Firefox prefer webm)
            const candidates = [
                "video/mp4;codecs=avc1,mp4a",
                "video/webm;codecs=vp9,opus",
                "video/webm;codecs=vp8,opus",
                "video/webm",
            ];
            const mimeType = candidates.find((m) => MediaRecorder.isTypeSupported?.(m)) || "";
            const opts = mimeType ? { mimeType, videoBitsPerSecond: 1500000 } : { videoBitsPerSecond: 1500000 };
            const mr = new MediaRecorder(stream, opts);
            recordedChunksRef.current = [];
            mr.ondataavailable = (e) => {
                if (e.data && e.data.size > 0) recordedChunksRef.current.push(e.data);
            };
            mr.start(2000); // gather chunks every 2s so we can recover partial recordings
            mediaRecorderRef.current = mr;
            recordingMimeRef.current = mimeType || "video/webm";
        } catch (err) {
            console.warn("MediaRecorder failed:", err);
        }
    }, []);

    const stopRecording = useCallback(() => {
        const mr = mediaRecorderRef.current;
        if (mr && mr.state !== "inactive") {
            try { mr.stop(); } catch { /* silent */ }
        }
        mediaRecorderRef.current = null;
    }, []);

    const finalizeRecordingBlob = useCallback(async () => {
        const mr = mediaRecorderRef.current;
        if (mr && mr.state !== "inactive") {
            await new Promise((resolve) => {
                mr.onstop = resolve;
                try { mr.stop(); } catch { resolve(); }
            });
        }
        const chunks = recordedChunksRef.current;
        if (!chunks.length) return null;
        const mime = recordingMimeRef.current || "video/webm";
        return new Blob(chunks, { type: mime });
    }, []);

    // Periodic frame scan (host only, camera mode) — every 30s send a snapshot to AI moderator
    useEffect(() => {
        if (!isHost) return;
        if (live?.using_wallpaper) return; // don't scan static wallpaper
        const SCAN_INTERVAL_MS = 30_000;
        let stopped = false;

        const scanOnce = async () => {
            if (stopped) return;
            // Use the local <video> if present (wallpaper-off legacy path) OR
            // any rendered LiveKit video on the stage.
            const v = videoRef.current || stageRef.current?.querySelector("video");
            if (!v || !v.videoWidth) return;
            try {
                const c = document.createElement("canvas");
                c.width = 480;
                c.height = Math.round((v.videoHeight / v.videoWidth) * 480) || 270;
                const ctx = c.getContext("2d");
                ctx.drawImage(v, 0, 0, c.width, c.height);
                const dataUrl = c.toDataURL("image/jpeg", 0.7);
                const b64 = dataUrl.split(",")[1] || "";
                const { data } = await api.post(`/live/${liveId}/scan`, { image_base64: b64 });
                if (data.flagged) {
                    if (data.suspended) {
                        toast.error(`Stream ended: ${data.categories?.join(", ") || "policy violation"}. Live access suspended.`);
                        // Backend already ended the stream; navigate away
                        nav("/live");
                    } else {
                        toast.warning(`⚠️ Warning ${data.warnings}/${data.limit}: ${data.categories?.join(", ") || "policy issue"}. Stream will end at ${data.limit}.`);
                    }
                }
            } catch (e) { /* silent */ }
        };

        // First scan after a short delay (let camera initialize)
        const initial = setTimeout(scanOnce, 8_000);
        const t = setInterval(scanOnce, SCAN_INTERVAL_MS);
        return () => {
            stopped = true;
            clearTimeout(initial);
            clearInterval(t);
        };
    }, [isHost, liveId, live?.using_wallpaper, nav]);

    const flushLikes = useCallback(() => {
        const n = tapPending.current;
        tapPending.current = 0;
        if (n <= 0) return;
        Promise.all(
            Array.from({ length: n }, () => api.post(`/live/${liveId}/like`).catch(() => null))
        ).then((results) => {
            const last = results.filter(Boolean).slice(-1)[0];
            if (last?.data?.likes_count != null) {
                setLive((cur) => cur ? { ...cur, likes_count: last.data.likes_count } : cur);
            }
        });
        setTimeout(() => setTapBurst(0), 600);
    }, [liveId]);

    const onStageTap = (e) => {
        if (!live || live.status !== "live") return;
        const rect = stageRef.current?.getBoundingClientRect();
        if (!rect) return;
        const x = (e.touches?.[0]?.clientX || e.clientX) - rect.left;
        const y = (e.touches?.[0]?.clientY || e.clientY) - rect.top;
        const hue = 200 + Math.floor(Math.random() * 30);
        const hid = ++heartId;
        const dx = (Math.random() * 60 - 30);
        const rotation = (Math.random() * 30 - 15);
        setHearts((h) => [...h, { id: hid, x, y, dx, rotation, hue }]);
        setTimeout(() => setHearts((h) => h.filter((p) => p.id !== hid)), 1400);
        setLive((cur) => cur ? { ...cur, likes_count: (cur.likes_count || 0) + 1 } : cur);
        tapPending.current += 1;
        setTapBurst((n) => n + 1);
        if (flushTimer.current) clearTimeout(flushTimer.current);
        flushTimer.current = setTimeout(flushLikes, 350);
    };

    useEffect(() => () => {
        if (flushTimer.current) clearTimeout(flushTimer.current);
    }, []);

    const onEndWithRecording = async (saveRecording) => {
        setEndingMode(true);
        try {
            let body = {};
            if (saveRecording) {
                const blob = await finalizeRecordingBlob();
                if (!blob || blob.size < 1024) {
                    toast.warning("No recording captured (stream too short or recording unsupported)");
                } else if (blob.size > 200 * 1024 * 1024) {
                    toast.warning("Recording exceeds 200MB — saved metadata only");
                } else {
                    const ext = recordingMimeRef.current.includes("mp4") ? "mp4" : "webm";
                    const file = new File([blob], `live-${liveId.slice(0, 8)}.${ext}`, { type: recordingMimeRef.current });
                    const form = new FormData();
                    form.append("file", file);
                    const { data: up } = await api.post("/upload", form, { headers: { "Content-Type": "multipart/form-data" } });
                    body = { recording_url: `${process.env.REACT_APP_BACKEND_URL}${up.url}`, storage_path: up.path || "" };
                }
            } else {
                stopRecording();
            }
            await api.post(`/live/${liveId}/end`, body);
            toast.success(body.recording_url ? "Stream ended — recording archived" : "Stream ended");
            nav("/live");
        } catch (err) {
            console.error(err);
            toast.error(formatErr(err.response?.data?.detail) || "Failed to end stream");
        } finally {
            setEndingMode(false);
        }
    };

    const onEndSelected = (saveRecording) => {
        setRecordPickerOpen(false);
        setSettingsOpen(false);
        onEndWithRecording(saveRecording);
    };

    if (!live) return <div className="p-10 text-wta-muted">Loading…</div>;

    const author = live.user || {};
    const wallpaper = live.using_wallpaper && live.wallpaper_url ? live.wallpaper_url : null;
    const guests = live.guests || [];
    const myInvite = !isHost && user ? guests.find((g) => g.user_id === user.id && g.status === "invited") : null;
    const showInviteModal = !!myInvite && !inviteHandled;

    const acceptInvite = async () => {
        setRespondingInvite(true);
        try {
            await api.post(`/live/${liveId}/guests/accept`);
            toast.success("You're on stage");
            setInviteHandled(true);
            const { data } = await api.get(`/live/${liveId}`);
            setLive(data);
        } catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
        finally { setRespondingInvite(false); }
    };

    const declineInvite = async () => {
        setRespondingInvite(true);
        try {
            await api.post(`/live/${liveId}/guests/decline`);
            toast.success("Invite declined");
            setInviteHandled(true);
            const { data } = await api.get(`/live/${liveId}`);
            setLive(data);
        } catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
        finally { setRespondingInvite(false); }
    };

    return (
        <div className="bg-black min-h-[100dvh] relative overflow-hidden">
            {/* Header overlay */}
            <div className="fixed top-0 inset-x-0 z-30 flex items-center justify-between p-3 bg-gradient-to-b from-black/80 to-transparent">
                <button data-testid="live-back" onClick={() => nav("/live")} className="text-white flex items-center gap-1 text-sm">
                    <ArrowLeft size={16} /> Back
                </button>
                <div className="flex items-center gap-2">
                    {live.status === "live" && (
                        <span className="px-2 py-0.5 text-[10px] font-heading uppercase tracking-widest rounded-sm bg-wta-primary text-white live-pulse">LIVE</span>
                    )}
                    <div className="glass px-2 py-0.5 rounded-sm text-xs flex items-center gap-1">
                        <Users size={12} /> {live.viewers_count || 0}
                    </div>
                    <div data-testid="live-likes-count" className="glass px-2.5 py-1 rounded-full text-xs flex items-center gap-1 text-wta-primary font-heading tracking-wider">
                        <Heart size={14} weight="fill" /> {live.likes_count || 0}
                    </div>
                </div>
                {isHost ? (
                    <button
                        data-testid="live-host-settings"
                        onClick={() => setSettingsOpen(true)}
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-wta-primary/20 border border-wta-primary/60 text-wta-primary hover:bg-wta-primary/30 transition"
                        aria-label="Live settings"
                    >
                        <GearSix size={16} weight="bold" />
                        <span className="text-[10px] font-heading tracking-widest uppercase">Settings</span>
                    </button>
                ) : <div className="w-10" />}
            </div>

            {/* Topic banner */}
            {live.topic && (
                <div data-testid="live-topic-banner" className="absolute top-12 left-1/2 -translate-x-1/2 z-20 max-w-[90%] px-4 py-2 rounded-full bg-wta-primary/15 backdrop-blur-md border border-wta-primary/40">
                    <div className="font-heading text-sm tracking-wide text-white truncate">{live.topic}</div>
                </div>
            )}

            {/* Guest stage roster (PiP slots) — always 2, placeholders if empty */}
            <div data-testid="guest-stage-roster" className="absolute right-3 top-16 md:top-20 z-20 flex flex-col gap-2">
                {Array.from({ length: GUEST_MAX }).map((_, i) => {
                    const g = guests[i];
                    if (!g) {
                        return (
                            <div
                                key={i}
                                data-testid={`guest-pip-empty-${i}`}
                                className="w-20 h-28 sm:w-24 sm:h-32 rounded-sm border border-dashed border-white/20 bg-black/40 backdrop-blur-sm flex flex-col items-center justify-center text-white/40"
                            >
                                <UserPlus size={20} />
                                <span className="text-[9px] tracking-widest uppercase mt-1">Slot {i + 1}</span>
                            </div>
                        );
                    }
                    const isOnStage = g.status === "on_stage";
                    return (
                        <div
                            key={g.user_id}
                            data-testid={`guest-pip-${i}`}
                            className={`relative w-20 h-28 sm:w-24 sm:h-32 rounded-sm overflow-hidden border ${isOnStage ? "border-wta-primary" : "border-yellow-400/70"} bg-black/60`}
                        >
                            <div className="w-full h-full flex items-center justify-center font-heading text-2xl bg-gradient-to-br from-wta-surface to-black">
                                {g.user?.profile_pic ? (
                                    <img src={g.user.profile_pic} alt="" className="w-full h-full object-cover" />
                                ) : (g.user?.name?.[0]?.toUpperCase() || "?")}
                            </div>
                            <div className="absolute top-1 left-1 right-1 px-1 py-0.5 rounded-sm bg-black/70 text-[8px] tracking-widest font-heading uppercase text-center truncate">
                                {isOnStage ? <span className="text-wta-primary">on stage</span> : <span className="text-yellow-400">invited</span>}
                            </div>
                            <div className="absolute inset-x-0 bottom-0 px-1 py-1 bg-black/80 text-white text-[10px] truncate text-center">
                                @{g.user?.name}
                            </div>
                        </div>
                    );
                })}
            </div>

            {/* Stage — tap-zone */}
            <div
                ref={stageRef}
                data-testid="live-stage"
                onClick={onStageTap}
                className="absolute inset-0 select-none cursor-pointer"
            >
                {wallpaper ? (
                    <img data-testid="live-wallpaper" src={wallpaper} alt="" className="absolute inset-0 w-full h-full object-cover" />
                ) : liveKitEnabled ? (
                    <LiveKitStage
                        liveId={liveId}
                        enabled={liveKitEnabled}
                        shouldPublish={shouldPublish}
                        authorName={author.name}
                        fallback={
                            isHost ? (
                                <video
                                    ref={videoRef}
                                    data-testid="live-host-video"
                                    autoPlay
                                    playsInline
                                    muted
                                    className="absolute inset-0 w-full h-full object-cover"
                                    style={{ transform: live.facing_mode === "environment" ? "none" : "scaleX(-1)" }}
                                />
                            ) : (
                                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                                    <div className="text-center">
                                        <Broadcast size={56} weight="fill" className="text-wta-primary mx-auto" />
                                        <div className="font-heading text-3xl tracking-wide mt-3 text-white/80">@{author.name}</div>
                                        <div className="text-xs tracking-[0.3em] uppercase text-wta-muted mt-1">Streaming live</div>
                                    </div>
                                </div>
                            )
                        }
                    />
                ) : isHost ? (
                    <video
                        ref={videoRef}
                        data-testid="live-host-video"
                        autoPlay
                        playsInline
                        muted
                        className="absolute inset-0 w-full h-full object-cover"
                        style={{ transform: live.facing_mode === "environment" ? "none" : "scaleX(-1)" }}
                    />
                ) : (
                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                        <div className="text-center">
                            <Broadcast size={56} weight="fill" className="text-wta-primary mx-auto" />
                            <div className="font-heading text-3xl tracking-wide mt-3 text-white/80">@{author.name}</div>
                            <div className="text-xs tracking-[0.3em] uppercase text-wta-muted mt-1">Streaming live</div>
                        </div>
                    </div>
                )}

                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-black/40 pointer-events-none" />

                {/* Floating hearts */}
                {hearts.map((h) => (
                    <span
                        key={h.id}
                        className="pointer-events-none absolute live-heart"
                        style={{ left: `${h.x}px`, top: `${h.y}px`, "--dx": `${h.dx}px`, "--rot": `${h.rotation}deg`, color: `hsl(${h.hue}, 95%, 60%)` }}
                    >
                        <Heart size={42} weight="fill" />
                    </span>
                ))}
            </div>

            {/* Group chat overlay (above bottom info bar) */}
            {user && (
                <LiveChat
                    liveId={liveId}
                    currentUser={user}
                    onOpenGift={isHost ? null : () => setGiftOpen(true)}
                    onGiftMessage={(m) => giftFxRef.current?.play({
                        animation: m.gift_animation || "float-up",
                        emoji: m.gift_emoji || "🎁",
                        image_url: m.gift_image_url || "",
                        color: m.gift_color || "#F59E0B",
                        name: m.gift_name || m.gift_type,
                        coins: m.gift_value,
                        senderName: m.user?.name,
                    })}
                />
            )}

            {/* Gift animation overlay — plays for all viewers */}
            <GiftAnimation ref={giftFxRef} />

            {/* Bottom info bar */}
            <div className="absolute left-0 right-0 bottom-0 z-10 p-4 pb-8 bg-gradient-to-t from-black/85 to-transparent">
                <div className="flex items-center justify-between gap-3">
                    <Link to={`/profile/${author.id}`} className="flex items-center gap-3 min-w-0 flex-1">
                        <div className="w-10 h-10 rounded-full bg-wta-surface2 border border-white/30 flex items-center justify-center font-heading text-lg overflow-hidden shrink-0">
                            {author.profile_pic ? <img src={author.profile_pic} alt="" className="w-full h-full object-cover" /> : (author.name?.[0]?.toUpperCase() || "?")}
                        </div>
                        <div className="min-w-0">
                            <div className="font-heading text-lg tracking-wide truncate">@{author.name}</div>
                            <div className="text-[10px] tracking-widest uppercase text-wta-muted">Tap anywhere to send love</div>
                        </div>
                    </Link>
                    {/* Gifts total + viewer Gift CTA */}
                    {(live.gifts_value || 0) > 0 && (
                        <div data-testid="live-gifts-total" className="hidden sm:flex glass px-2.5 py-1 rounded-full text-xs items-center gap-1 text-wta-accent font-heading tracking-wider">
                            <Coins size={12} weight="fill" /> {live.gifts_value}
                        </div>
                    )}
                    {user && !isHost && (
                        <button
                            type="button"
                            data-testid="live-send-gift-btn"
                            onClick={(e) => { e.stopPropagation(); setGiftOpen(true); }}
                            className="shrink-0 px-3 py-2 mr-12 sm:mr-0 rounded-full bg-wta-accent/20 border border-wta-accent/60 text-wta-accent font-heading text-xs tracking-wider uppercase hover:bg-wta-accent/30 flex items-center gap-1.5"
                        >
                            <Coins size={14} weight="fill" /> Gift
                        </button>
                    )}
                </div>
            </div>

            {tapBurst > 0 && (
                <div key={tapBurst} className="pointer-events-none absolute top-1/3 left-1/2 -translate-x-1/2 z-20 wta-tap-burst font-heading text-4xl text-wta-primary">
                    +{tapBurst}
                </div>
            )}

            {/* Hidden file input for end & save */}
            <input ref={fileRef} type="file" accept="video/*" className="hidden" data-testid="live-end-recording-file" />

            {/* Guest invite modal — shown when current user has a pending invite for this stream */}
            {showInviteModal && (
                <div data-testid="guest-invite-modal" className="fixed inset-0 z-[85] bg-black/85 flex items-center justify-center p-4">
                    <div className="bg-wta-surface border border-wta-primary/60 rounded-sm p-6 max-w-sm w-full text-center">
                        <UserPlus size={40} weight="fill" className="text-wta-primary mx-auto mb-3" />
                        <div className="font-heading text-2xl tracking-wider uppercase mb-1">You're invited</div>
                        <div className="text-sm text-wta-muted mb-5">
                            <span className="text-white">@{author.name}</span> wants you on stage.
                            {live.topic && <div className="mt-1 text-xs italic">"{live.topic}"</div>}
                        </div>
                        <div className="flex gap-2">
                            <button
                                data-testid="guest-invite-decline"
                                disabled={respondingInvite}
                                onClick={declineInvite}
                                className="flex-1 btn-ghost py-2.5 rounded-sm text-sm flex items-center justify-center gap-1 disabled:opacity-50"
                            >
                                <X size={14} weight="bold" /> Decline
                            </button>
                            <button
                                data-testid="guest-invite-accept"
                                disabled={respondingInvite}
                                onClick={acceptInvite}
                                className="flex-1 btn-primary py-2.5 rounded-sm text-sm flex items-center justify-center gap-1 disabled:opacity-50"
                            >
                                <Check size={14} weight="bold" /> Accept
                            </button>
                        </div>
                        <div className="text-[10px] text-wta-muted mt-3">Invite expires in 5 min</div>
                    </div>
                </div>
            )}

            {/* Live settings drawer */}
            <LiveSettingsDrawer
                open={settingsOpen}
                live={live}
                onClose={() => setSettingsOpen(false)}
                onUpdate={(updated) => setLive(updated)}
                onEnd={() => setRecordPickerOpen(true)}
            />

            {/* Gift picker (viewers gifting the host) */}
            {giftOpen && (
                <GiftPicker
                    receiverId={author.id}
                    liveId={liveId}
                    onSent={(gift) => {
                        giftFxRef.current?.play({
                            animation: gift.animation || "float-up",
                            emoji: gift.emoji || "🎁",
                            image_url: gift.image_url || "",
                            color: gift.color || "#F59E0B",
                            name: gift.name,
                            coins: gift.cost,
                            senderName: user?.name,
                        });
                    }}
                    onClose={() => {
                        setGiftOpen(false);
                        // Refresh live to pick up updated gifts_value
                        api.get(`/live/${liveId}`).then(({ data }) => setLive(data)).catch(() => {});
                    }}
                />
            )}

            {/* End-stream prompt */}
            {recordPickerOpen && (
                <div data-testid="end-record-prompt" className="fixed inset-0 z-[80] bg-black/80 flex items-center justify-center p-4" onClick={() => setRecordPickerOpen(false)}>
                    <div className="bg-wta-surface border border-wta-border rounded-sm p-5 max-w-sm w-full" onClick={(e) => e.stopPropagation()}>
                        <div className="font-heading text-lg uppercase tracking-wider mb-2">End live</div>
                        <div className="text-sm text-wta-muted mb-4">Auto-recording captured during your stream will be saved to your archive (auto-deleted in 48h). Or just end without saving?</div>
                        <div className="flex gap-2">
                            <button data-testid="end-without-save" disabled={endingMode} onClick={() => onEndSelected(false)} className="flex-1 btn-ghost py-2 rounded-sm text-sm">{endingMode ? "Ending…" : "Just end"}</button>
                            <button data-testid="end-with-save" disabled={endingMode} onClick={() => onEndSelected(true)} className="flex-1 btn-primary py-2 rounded-sm text-sm">{endingMode ? "Saving…" : "End & Save"}</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
